"use client";

import { useEffect, useState } from "react";
import { ColoursUI } from "./ColoursUI";
import { useVariant } from "@/hooks/useVariant/useVariant.ts";
import { useRouter } from "next/router";
import { ColourOption } from "@/components/Colours/types.ts";
import {
  getItemsCountToDisplay,
  sortColourOptions,
  transformToColourOptions,
} from "@/components/Colours/helpers.ts";
import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { COLOUR_SELECTOR_SHOW_MORE_BUTTON_CLICK } from "@/components/Colours/ColourList/monetateEvents.ts";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId.ts";
import {
  standardComponent,
  useStandardProps,
} from "@/context/StandardPropsContext/StandardPropsContext";

const isMobileWidth = () => window.innerWidth < 768;

export const Colours = standardComponent("Colours", () => {
  const { configProps, flags, product, runtimeConfig, experiencesRawObject } =
    useStandardProps();
  const { variant } = useVariant(product);
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();
  const { monetateId } = useMonetateId();
  const { brand } = product;

  const showPromoOnIndicators = !!flags.showPromoOnIndicators.value;
  const useSimplifiedColourSelector = flags.useSimplifiedColourSelector.value;
  const ignoreOutOfStockVariantsOnPromo =
    flags.ignoreOutOfStockVariantsOnPromo.value === true;
  const replaceHistoryEntryOnVariantChange =
    flags.replaceHistoryEntryOnVariantChange.value === true;

  const useSimplifiedVersion = Boolean(
    configProps?.useSimplifiedVersion || useSimplifiedColourSelector,
  );

  useEffect(() => {
    setIsMobile(isMobileWidth());
    const handleResize = () =>
      isMobileWidth() ? setIsMobile(true) : setIsMobile(false);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const variantGroupsHaveColours = !!product.variantGroups?.[0]?.value;

  const colourOptions: ColourOption[] = transformToColourOptions(
    product,
    variant.differentiators?.colour || "",
    router.query,
    {
      showPromoOnIndicators,
      ignoreOutOfStockVariantsOnPromo,
    },
  );

  const sortedColourOptions = sortColourOptions(colourOptions);

  const rowsToDisplay = getItemsCountToDisplay(
    isMobile,
    sortedColourOptions,
    variant,
  );

  const someVariantsInStock = sortedColourOptions.some(
    (colourOption) => colourOption.showAsAvailable,
  );

  useEffect(() => {
    if (colourOptions.length > 1 && someVariantsInStock) {
      const allAvailableColoursOnPromoOrIgnored = colourOptions
        .filter((c) => c.showAsAvailable)
        .every((c) => c.showReduced || c.reducedButIgnored);

      const hasFullyOosColour = colourOptions.some((c) => !c.showAsAvailable);

      if (allAvailableColoursOnPromoOrIgnored && hasFullyOosColour) {
        fireManagedImpression(true);
      } else {
        fireManagedImpression(false);
      }
    } else {
      fireManagedImpression(false);
    }
  }, [product]);

  const fireManagedImpression = (value: boolean) => {
    recordImpressionByParam(
      runtimeConfig?.monetateEnvironment,
      experiencesRawObject,
      "pdp-scaffold-observeColourPromotion",
      value ? "true" : "false",
      monetateId,
    );
  };

  useEffect(() => {
    const handleClick = (e: Event) => {
      const target = e.target;
      if (target)
        dispatchMonetateQEvent(COLOUR_SELECTOR_SHOW_MORE_BUTTON_CLICK);
    };

    const colourContainer = document.querySelector(
      '[data-component="Colours"] > div > button',
    );

    colourContainer?.addEventListener("click", handleClick);

    return () => {
      colourContainer?.removeEventListener("click", handleClick);
    };
  }, []);

  if (!variantGroupsHaveColours) return null;
  if (colourOptions?.length <= 1 && !useSimplifiedVersion) return null;

  return (
    <ColoursUI
      colourOptions={sortedColourOptions}
      rowsToDisplay={rowsToDisplay}
      brand={brand?.name}
      useSimplifiedVersion={useSimplifiedVersion}
      replaceHistoryEntryOnVariantChange={replaceHistoryEntryOnVariantChange}
    />
  );
});
